
import { computed, defineComponent, onMounted, reactive, toRefs, ref, PropType, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import ContentAdaptLayout from '@/views/dashboard/_components/content-adapt-layout/content-adapt-layout.vue'
import {
  AllBaseLineItemResponse,
  RepairTreeItem,
  ScanLevelType
} from '@/views/dashboard/safe-status/base-line/repair-item-tree-type'
import {
  BaselineScanService
} from '@/views/dashboard/_components/group-policy-settings/_components/baseline-scan/baseline-scan-service'
import { GroupPolicySettingsService } from '@/views/dashboard/_components/group-policy-settings/group-policy-settings.service'
import {
  applyBaselineRepairDisplayNamesToTree,
  DUBA_BASELINE_REPAIR_ITEMS_KEY,
  ensureBaselineRepairItemsLoaded,
  setCustomThresholds
} from '@/views/dashboard/safe-status/base-line/baseline-repair-items-cache'
import { SystemConfigService } from '@/services/system-config.service'
import { ElMessage } from 'element-plus'
import type { BaselineScanCustomItem } from '@/views/dashboard/_components/group-policy-settings/group-policy-settings.service'

interface CustomValueMeta {
  itemId: string
  min: number
  max: number
  fixValue: number
}

const CUSTOMIZABLE_ITEMS: CustomValueMeta[] = [
  { itemId: '1.1.3', min: 0, max: 14, fixValue: 10 },
  { itemId: '1.1.4', min: 0, max: 998, fixValue: 2 },
  { itemId: '1.1.5', min: 0, max: 999, fixValue: 30 },
  { itemId: '1.1.6', min: 0, max: 24, fixValue: 5 },
  { itemId: '1.2.1', min: 0, max: 120, fixValue: 30 },
  { itemId: '1.2.2', min: 0, max: 999, fixValue: 4 },
  { itemId: '1.2.3', min: 0, max: 120, fixValue: 30 },
]

const CUSTOMIZABLE_ITEM_DEFAULTS: Record<string, number> = {
  '1.1.3': 10,
  '1.1.4': 2,
  '1.1.5': 30,
  '1.1.6': 5,
  '1.2.1': 30,
  '1.2.2': 4,
  '1.2.3': 30,
}

const CUSTOMIZABLE_ITEM_IDS = new Set(CUSTOMIZABLE_ITEMS.map(i => i.itemId))

/** 可编辑值在逗号后面的项（模式B：data_value: '0,<val>'，fix_value: <val>） */
const SECOND_POSITION_ITEMS = new Set(['1.1.5', '1.2.2'])

export default defineComponent({
  components: {
    ContentAdaptLayout
  },
  props: {
    operateType: {
      type: String as PropType<'edit'|'add'|'read'>,
      default: 'edit'
    },
    scan_type: {
      type: Number,
      default: 0
    },
    system_tab: {
      type: String,
      default: '1'
    },
    scan_items_id: {
      type: Array as PropType<string[]>,
      default: () => ([] as string[])
    },
    scan_item_custom: {
      type: Array as PropType<BaselineScanCustomItem[]>,
      default: () => ([] as BaselineScanCustomItem[])
    }
  },
  emits: ['handleScanDrawerClose'],
  setup (props, context) {
    const { t, locale } = useI18n()
    const injector = useReflectiveInjector([BaselineScanService, SystemConfigService])
    const service = injector.get(BaselineScanService)
    const groupPolicySettingsService = injector.get(GroupPolicySettingsService)
    const systemConfigService = injector.get(SystemConfigService)
    const treeRef = ref(null)
    const data = reactive({
      loading: false,
      btnStatus: true,
      scanType: props.scan_type || 0,
      ALL_ITEMS: [],
      ORIGINAL_ITEMS: [],
      selectKeys: props.scan_items_id || [],
      editMode: false,
      customValues: {} as Record<string, number>,
      validationConstraintError: false,
      checkCount: {
        1: 0,
        2: 0,
        3: 0,
        4: 0,
        100: 0,
        101: 0,
        102: 0,
        103: 0,
        104: 0
      }
    })
    function getData () {
      const raw = localStorage.getItem(DUBA_BASELINE_REPAIR_ITEMS_KEY)
      if (!raw) return
      const allItem: AllBaseLineItemResponse = JSON.parse(raw)
      const named: AllBaseLineItemResponse = {
        ...allItem,
        category: applyBaselineRepairDisplayNamesToTree(allItem.category),
        linux_category: applyBaselineRepairDisplayNamesToTree(allItem.linux_category),
      }
      data.ORIGINAL_ITEMS = props.system_tab === '1' ? named.category : named.linux_category
      data.ALL_ITEMS = service.generateSecondTree(service.generateTreeFactory(named, props.system_tab))
      nextTick(() => {
        if (data.scanType === 0) {
          treeRef.value.setCheckedKeys(service.getAllBaseLineItemId(data.ORIGINAL_ITEMS))
        } else {
          treeRef.value.setCheckedKeys(props.scan_items_id)
        }
        Array.of('1', '2', '3', '4', '100', '101', '102', '103', '104').forEach(i => {
          data.checkCount[i] = treeRef.value.getNode(i)?.childNodes.filter((item:any) => item.checked).length
        })
      })
    }
    /** 勾选集与目标集合是否完全一致（忽略顺序） */
    function isSameKeySet (checkedKeys: any[], targetKeys: any[]) {
      return checkedKeys.length === targetKeys.length && checkedKeys.every(id => targetKeys.includes(id))
    }
    /** 根据勾选结果自动推导扫描范围：全部勾选=全部，勾选等于常规项=常规，其余=自定义 */
    function syncScanTypeByCheckedKeys () {
      const checkedKeys = treeRef.value.getCheckedKeys(true)
      if (isSameKeySet(checkedKeys, service.getAllBaseLineItemId(data.ORIGINAL_ITEMS))) {
        data.scanType = ScanLevelType.ALL
      } else if (isSameKeySet(checkedKeys, service.getNormalItemsId(data.ORIGINAL_ITEMS))) {
        data.scanType = ScanLevelType.NORMAL
      } else {
        data.scanType = ScanLevelType.CUSTOM
      }
    }
    function updateSelect (node: RepairTreeItem) {
      data.btnStatus = false
      syncScanTypeByCheckedKeys()
      groupPolicySettingsService.updateBaselineScanType(data.scanType)
      groupPolicySettingsService.updateBaselineScanItemKeys(treeRef.value.getCheckedKeys())
      data.checkCount[node.id.split('.')[0]] = treeRef.value.getNode(node.id.split('.')[0]).childNodes.filter((item:any) => item.checked).length
      groupPolicySettingsService.updateEditTab(true)
    }
    function initCustomValues () {
      CUSTOMIZABLE_ITEMS.forEach(meta => {
        data.customValues[meta.itemId] = meta.fixValue
      })
    }

    function applyCustomValuesFromArray (items: BaselineScanCustomItem[]): Record<string, number> {
      const apiFixValues: Record<string, number> = {}
      const thresholds: Record<string, number> = {}
      if (!items?.length) return apiFixValues
      const parseLastSegment = (raw: string): number => {
        const parts = String(raw || '').split(',').filter(p => p !== '')
        return parseFloat(parts.length > 0 ? parts[parts.length - 1] : '')
      }
      items.forEach((item: BaselineScanCustomItem) => {
        const dataVal = parseLastSegment(item.data_value || '')
        if (!isNaN(dataVal)) {
          data.customValues[item.item_id] = dataVal
          thresholds[item.item_id] = dataVal
        }
        const fixVal = parseLastSegment(item.fix_value || '')
        if (!isNaN(fixVal)) {
          apiFixValues[item.item_id] = fixVal
        }
      })
      setCustomThresholds(thresholds)
      return apiFixValues
    }

    async function fetchCustomValues () {
      let apiFixValues: Record<string, number> = {}
      try {
        const res = await systemConfigService.getSystemConfig({ config_key: 'base_lime_custom' })
        if (res?.config_value) {
          const parsed = JSON.parse(res.config_value)
          if (Array.isArray(parsed)) {
            apiFixValues = applyCustomValuesFromArray(parsed)
            console.log('apiFixValues :>> ', apiFixValues);
          }
        }
      } catch (e) {
        console.warn('Failed to fetch custom baseline values', e)
      }
      // 缺失的项优先用 API fix_value，否则用硬编码默认值
      CUSTOMIZABLE_ITEMS.forEach(meta => {
        if (data.customValues[meta.itemId] === undefined) {
          data.customValues[meta.itemId] = apiFixValues[meta.itemId] ?? meta.fixValue
        }
      })
    }

    function buildDataValue (meta: CustomValueMeta): string {
      const customVal = data.customValues[meta.itemId] ?? meta.fixValue
      if (SECOND_POSITION_ITEMS.has(meta.itemId)) {
        return '0,' + customVal
      }
      return customVal + ','
    }

    function buildFixValue (meta: CustomValueMeta): string {
      return String(data.customValues[meta.itemId] ?? meta.fixValue)
    }

    function toggleEditMode () {
      // 退出编辑模式时保存自定义值
      if (data.editMode) {
        const customItems = CUSTOMIZABLE_ITEMS.map(meta => ({
          item_id: meta.itemId,
          data_value: buildDataValue(meta),
          fix_value: buildFixValue(meta),
        }))
        systemConfigService.editSystemConfig({
          config_key: 'base_lime_custom',
          config_value: JSON.stringify(customItems),
        }).then(() => {
          ElMessage.success(t('common.success'))
        })
      }
      data.editMode = !data.editMode
      syncTreeNodeNames()
      // Disable checkboxes for customizable items in edit mode
      function walkItems (items: any[]) {
        for (const item of items) {
          if (CUSTOMIZABLE_ITEM_IDS.has(item.id)) {
            item.disabled = data.editMode
          }
          if (item.items) walkItems(item.items)
        }
      }
      walkItems(data.ALL_ITEMS)
    }

    function parseLabelParts (label: string): { prefix: string; num: string; suffix: string } {
      const match = label.match(/(\d+)/)
      if (match) {
        const idx = match.index!
        const num = match[1]
        return {
          prefix: label.slice(0, idx),
          num,
          suffix: label.slice(idx + num.length),
        }
      }
      return { prefix: label, num: '', suffix: '' }
    }

    function resetCustomValue (itemId: string) {
      if (CUSTOMIZABLE_ITEM_DEFAULTS[itemId] !== undefined) {
        data.customValues[itemId] = CUSTOMIZABLE_ITEM_DEFAULTS[itemId]
        validateConstraints()
        syncTreeNodeNames()
      }
    }

    function validateConstraints () {
      const minAge = data.customValues['1.1.4']
      const maxAge = data.customValues['1.1.5']
      data.validationConstraintError = minAge !== undefined && maxAge !== undefined && minAge >= maxAge
    }

    function syncTreeNodeNames () {
      if (!data.ALL_ITEMS?.length) return
      const I18N_KEYS: Record<string, string> = {
        '1.1.3': 'baselineRepairItems.n.1_1_3',
        '1.1.4': 'baselineRepairItems.n.1_1_4',
        '1.1.5': 'baselineRepairItems.n.1_1_5',
        '1.1.6': 'baselineRepairItems.n.1_1_6',
        '1.2.1': 'baselineRepairItems.n.1_2_1',
        '1.2.2': 'baselineRepairItems.n.1_2_2',
        '1.2.3': 'baselineRepairItems.n.1_2_3',
      }
      const walk = (items: any[]) => {
        for (const item of items) {
          const key = I18N_KEYS[item.id]
          if (key && item.fatherName) {
            item.name = t(key, [data.customValues[item.id]])
          }
          if (item.items) walk(item.items)
        }
      }
      walk(data.ALL_ITEMS)
    }

    function isCustomizableItem (id: string): boolean {
      return CUSTOMIZABLE_ITEM_IDS.has(id)
    }

    function getCustomItemMeta (itemId: string): CustomValueMeta | undefined {
      return CUSTOMIZABLE_ITEMS.find(i => i.itemId === itemId)
    }

    const selectOptions = computed(() => {
      void locale.value
      return {
        1: [
          { key: 0, value: 0, label: t('groupPolicySettings.scanOptAll') },
          { key: 1, value: 1, label: t('groupPolicySettings.scanOptNormal') },
          { key: 2, value: 2, label: t('groupPolicySettings.scanOptCustom') },
        ],
        2: [
          { key: 0, value: 0, label: t('groupPolicySettings.scanOptAll') },
          { key: 2, value: 2, label: t('groupPolicySettings.scanOptCustom') },
        ],
      }
    })
    onMounted(async () => {
      initCustomValues()
      await ensureBaselineRepairItemsLoaded(() => service.getAllBaselineItems().then(() => undefined))
      getData()
      // Apply saved strategy values first (before API fetch so they take priority)
      if (props.scan_item_custom?.length) {
        applyCustomValuesFromArray(props.scan_item_custom)
      }else{
        await fetchCustomValues()
      }
      syncTreeNodeNames()
    })
    return {
      t,
      ...toRefs(data),
      selectOptions,
      treeRef,
      toggleEditMode,
      resetCustomValue,
      validateConstraints,
      isCustomizableItem,
      getCustomItemMeta,
      parseLabelParts,
      defaultProps: {
        children: 'items',
        label: 'name',
      },
      async changeSelect (val: number) {
        data.btnStatus = false
        if (val === ScanLevelType.ALL) {
          // console.log(service.getAllBaseLineItemId(data.ORIGINAL_ITEMS).length)
          await treeRef.value.setCheckedKeys(service.getAllBaseLineItemId(data.ORIGINAL_ITEMS))
        } else if (val === ScanLevelType.NORMAL) {
          await treeRef.value.setCheckedKeys(service.getNormalItemsId(data.ORIGINAL_ITEMS))
        } else {
          await treeRef.value.setCheckedKeys(groupPolicySettingsService.customBaselineScanItemKeys)
        }
        groupPolicySettingsService.updateBaselineScanType(val)
        data.scanType = val
      },
      saveScanItemIds () {
        if (data.validationConstraintError) {
          ElMessage.warning(t('groupPolicySettings.scanCustomConstraintError'))
          return
        }
        groupPolicySettingsService.updateBaselineScanItemKeys(treeRef.value.getCheckedKeys())
        // Build custom items array
        const customItems: BaselineScanCustomItem[] = CUSTOMIZABLE_ITEMS.map(meta => ({
          item_id: meta.itemId,
          data_value: buildDataValue(meta),
          fix_value: buildFixValue(meta),
        }))
        groupPolicySettingsService.updateBaselineScanCustomValues(customItems)
        context.emit('handleScanDrawerClose')
      },
      nodeCheck (node: RepairTreeItem) {
        updateSelect(node)
      },
      changeCheck (node: RepairTreeItem) {
        updateSelect(node)
      }
    }
  }
})
